"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Standalone public legal pages, one real URL per document:
 * /datenschutz, /bedingungen, /impressum. The landing footer and the auth
 * dialog's Terms/Privacy links navigate here; the full text renders on
 * the page itself (no modal). No auth, no personal context.
 */

import { useEffect, useState, type ReactNode } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { BrandMarkV2 } from "@/components/landing/BrandMarkV2"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { LegalBody } from "@/components/landing/LegalBody"
import { useI18n } from "@/i18n"
import { PageEntrance } from "@/components/PageEntrance"
import {
  LEGACY_LEGAL_PANELS,
  LEGAL_DOCUMENT_TITLE_KEYS,
  LEGAL_PATHS,
  LEGAL_TITLE_KEYS,
  type LegalPanel,
} from "@/lib/legalRoutes"

export default function LegalPage({ panel }: { panel: LegalPanel }) {
  const { t } = useI18n()
  const title = t(LEGAL_DOCUMENT_TITLE_KEYS[panel])
  const [prerendered] = useState(
    () => document.getElementById("root")?.hasAttribute("data-prerendered") === true
  )

  useEffect(() => {
    const previousTitle = document.title
    document.title = `${title} — ${t("common.appName")}`
    return () => {
      document.title = previousTitle
    }
  }, [title, t])

  return (
    <PageEntrance skip={prerendered}>
      <LegalDocument panel={panel} />
    </PageEntrance>
  )
}

/**
 * The page itself, with no entrance animation and no browser-only effects, so
 * scripts/prerender-legal.mjs can render it to static HTML that is fully visible
 * without JavaScript (PageEntrance starts at opacity 0 until JS animates it).
 * `languageControl` replaces the language toggle (a button that needs JavaScript):
 * the prerender passes plain links to the other language's section instead.
 */
export function LegalDocument({
  panel,
  languageControl,
}: {
  panel: LegalPanel
  languageControl?: ReactNode
}) {
  const { t } = useI18n()
  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="border-b border-line bg-paper">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-semibold text-ink-2 hover:text-ink"
          >
            <BrandMarkV2 size={22} />
            <span>{t("common.appName")}</span>
          </Link>
          {languageControl ?? <LanguageToggle />}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-8 md:py-14">
        <h1 className="mb-6 text-3xl font-bold text-ink">{t(LEGAL_TITLE_KEYS[panel])}</h1>
        <LegalBody panel={panel} />
      </main>
    </div>
  )
}

/** /legal/privacy|terms|imprint → the canonical path; anything else → home. */
export function LegacyLegalRedirect() {
  const { panel } = useParams()
  if (!panel || !LEGACY_LEGAL_PANELS.has(panel)) return <Navigate to="/" replace />
  return <Navigate to={LEGAL_PATHS[panel as LegalPanel]} replace />
}
